import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { ReferralActivationStatus, type ReferralGetFromSlugRecord } from '@navio/nsdk';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiInfoBanner, NoddiLink, NoddiSpinner } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { useEffect, useRef } from 'react';
import { BenefitsActivatedCard } from '../Common/BenefitsActivatedCard';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';

interface Props {
  referral: ReferralGetFromSlugRecord;
  userGroupId: number;
  status: ReferralActivationStatus;
}

export function ActivateReferral({ referral, userGroupId, status }: Props) {
  const attemptedActivation = useRef(false);
  const {
    data: activation,
    mutate: activateReferral,
    isPending: isActivationPending,
    error: activationError
  } = noddiAsync.usePost({
    type: URLKeys.postActivateReferral,
    queryConfig: {
      onSettled: () => {
        void invalidateQuery({ urlKey: URLKeys.getReferralEligibility });
        void invalidateQuery({ urlKey: URLKeys.getUserGroupCoupons });
      }
    }
  });

  useEffect(() => {
    if (status !== ReferralActivationStatus.ELIGIBLE || attemptedActivation.current) {
      return;
    }
    attemptedActivation.current = true;
    activateReferral({ userGroupId, referralId: referral.id, brandDomain: referral.brandDomain });
  }, [status, activateReferral, userGroupId, referral.id, referral.brandDomain]);

  if (status === ReferralActivationStatus.ACTIVATED || activation) {
    return activation?.data.createdCouponIds.length ? (
      <BenefitsActivatedCard />
    ) : (
      <div role='status'>
        <NoddiInfoBanner
          texts={[
            {
              value: t`This referral has already been activated for this account. Check your discounts for availability.`
            }
          ]}
          actionNode={
            <NoddiLink to={customerRoutes.discounts.getPath()} variant='outline' size='small' endIcon='ArrowRight'>
              <Trans>See your discounts</Trans>
            </NoddiLink>
          }
        />
      </div>
    );
  }

  const ineligibleMessages: Partial<Record<ReferralActivationStatus, string>> = {
    [ReferralActivationStatus.COMPLETED_BOOKING]: t`This referral discount is available before your first completed booking.`,
    [ReferralActivationStatus.PREVIOUS_BENEFIT]: t`This account has already received a referral benefit.`,
    [ReferralActivationStatus.UNAVAILABLE]: t`This referral program is currently not available.`
  };
  const ineligibleMessage = ineligibleMessages[status];

  return (
    <div className='flex flex-col gap-2' role='status'>
      {activationError && <ApiErrorMessageWithTrans error={activationError} />}
      {ineligibleMessage && <NoddiInfoBanner texts={[{ value: ineligibleMessage }]} />}
      {(isActivationPending || (status === ReferralActivationStatus.ELIGIBLE && !activationError)) && (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <NoddiSpinner size='small' />
          <Trans>Activating your discount…</Trans>
        </div>
      )}
      {!isActivationPending && activationError && status === ReferralActivationStatus.ELIGIBLE && (
        <NoddiButton
          className='w-full md:w-fit'
          onClick={() => activateReferral({ userGroupId, referralId: referral.id, brandDomain: referral.brandDomain })}
        >
          <Trans>Try again</Trans>
        </NoddiButton>
      )}
    </div>
  );
}
